<template>
  <div class="confirm">
    <b-container>
      <!--
        test flight_voucher_id: 11
        confirm_code: BLF7DZSX6
        http://localhost:8666/confirm/20/BLFPJ4MEW
      -->
      <section v-if="loading" class="section section--loader">
        <loader size="large" :show_timing_slot="true" />
      </section>

      <section v-else class="confirm__wrapper">
        <div class="confirm__heading">
          <h1 class="confirm__heading-title">Complimenti!</h1>
          <p class="confirm__heading-subtitle">
            La tua prenotazione è confermata.
            <span v-if="redirect">
              (verrai reindirizzato al tuo portale tra
              <strong>{{ redirect_counter > 0 ? redirect_counter : 0 }}</strong> secondi...)
            </span>
          </p>
          <p class="confirm__heading-desc">Torna su questa pagina per vedere eventuali aggiornamenti pre-partenza.</p>
        </div>

        <check-in-status
          v-if="automated_checkin_info"
          :status_summary="automated_checkin_info.status_summary"
          :flights="automated_checkin_info.flights"
        />
        <checkin-online-alert
          v-else-if="status === 'ISSUED'"
          :carrier_reference="voucher.carrierReference"
          :email_pnr="voucher.emailPnr"
          :contact_name_surname="voucher.contactNameSurname"
          :checkin_required="!!checkin_online_info"
        />

        <div class="confirm__elements confirm__elements--first">
          <reservation
            :contacts="contacts"
            :booking_reference="voucher.carrierReference"
            :assistance_code="confirm_code"
            :assistance_brand="brand"
          />
          <flight-summary :flight_offer="first_flight_offer" />
        </div>
        <div class="confirm__elements confirm__elements--second">
          <confirmation-status
            :status="status"
            :email="beneficiary.email"
            :phone_number="phone_number"
            :email_support="email_support"
          />
          <support-notice :confirm_code="confirm_code" :email="beneficiary.email" />
          <travelers :travelers="travelers" />
          <flight-bags :flight_offer="first_flight_offer" />
          <additional-services-booked
            v-if="first_flight_offer"
            :flight_offer="first_flight_offer"
            :travelers="travelers"
          />
          <!-- biglietti e documenti per passeggero (specifico della conferma) -->
          <tickets-documents
            :travelers="travelers"
            :tickets="tickets"
            :flight_offer="first_flight_offer"
          />

          <booking-instructions
            v-if="status === 'ISSUED'"
            :carrier_reference="voucher.carrierReference"
            :contact_name_surname="voucher.contactNameSurname"
            :email_pnr="voucher.emailPnr"
          />
        </div>
      </section>
    </b-container>
  </div>
</template>

<script>
import { get_confirmation } from "@liveflight/providers";
import { get_config, get_object_field, log } from "@/utilities/commons";
import AdditionalServicesBooked from "@liveflight/components/blocks/additional-services-booked.vue";
import Reservation from "@liveflight/components/blocks/reservation.vue";
import Travelers from "@liveflight/components/blocks/travelers.vue";
import FlightSummary from "@liveflight/components/blocks/flight-summary.vue";
import FlightBags from "@liveflight/components/blocks/flight-bags.vue";
import ConfirmationStatus from "@liveflight/components/blocks/confirm/confirmation-status.vue";
import TicketsDocuments from "@liveflight/components/blocks/confirm/tickets-documents.vue";
import CheckinOnlineAlert from "@liveflight/components/blocks/confirm/checkin-online-alert.vue";
import BookingInstructions from "@liveflight/components/blocks/confirm/booking-instructions.vue";
import SupportNotice from "@liveflight/components/blocks/confirm/support-notice.vue";
import CheckInStatus from "@liveflight/components/blocks/check-in/check-in-status.vue";

export default {
  name: "confirm",
  components: {
    "check-in-status": CheckInStatus,
    "additional-services-booked": AdditionalServicesBooked,
    reservation: Reservation,
    travelers: Travelers,
    "flight-summary": FlightSummary,
    "flight-bags": FlightBags,
    "confirmation-status": ConfirmationStatus,
    "tickets-documents": TicketsDocuments,
    "checkin-online-alert": CheckinOnlineAlert,
    "booking-instructions": BookingInstructions,
    "support-notice": SupportNotice,
  },
  props: {
    confirm_code: {
      type: String,
      default: "",
    },
    flight_voucher_id: {
      type: String,
      default: "",
    },
    ignore_redirect: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      loading: true,
      voucher: {},
      beneficiary: {},
      // Canonical normalized FlightOffers lifted to voucher root by the
      // provider; first_flight_offer is the [0] convenience for code
      // paths that reach into itineraries outside the per-offer v-for.
      flight_offers: [],
      first_flight_offer: {},
      travelers: [],
      // contatti a cui inviare le info del volo (shape order-body Amadeus)
      contacts: [],
      tickets: [],
      // Populated in mounted() via get_config so a runtime-loaded
      // $config isn't captured empty by data()'s eager evaluation.
      brand: "",
      phone_number: "",
      email_support: "",
      status: "",
      post_sell_notice: {},
      redirect: false,
      redirect_counter: 30,
      redirect_interval: null,
      checkin_online_info: "",
      automated_checkin_info: null,
    };
  },
  beforeDestroy() {
    if (this.redirect_interval) {
      clearInterval(this.redirect_interval);
      this.redirect_interval = null;
    }
  },
  mounted() {
    this.brand = get_config("site.brand", "");
    this.phone_number = get_config("site.phoneNumber", "");
    this.email_support = get_config("site.emailSupport", "");
    const _this = this;
    const orderConfirmedRedirectTo = get_config(
      "integration.orderConfirmedRedirectTo",
      false,
    );
    if (!this.ignore_redirect && orderConfirmedRedirectTo) {
      _this.redirect = true;
      // Decrement first so the visible counter never undershoots; clear
      // before navigating so no orphan tick fires after unmount.
      this.redirect_interval = setInterval(() => {
        _this.redirect_counter = _this.redirect_counter - 1;
        if (_this.redirect_counter <= 0) {
          clearInterval(_this.redirect_interval);
          _this.redirect_interval = null;
          window.location.href = orderConfirmedRedirectTo;
        }
      }, 1000);
    }
    get_confirmation(this.$store.state.liveflight.flight_offer, {
      flightVoucherId: this.flight_voucher_id,
      confirmcode: this.confirm_code,
    })
      .then(
        (response) => {
          this.flight_offers = get_object_field(
            response,
            "voucher.flight_offers",
            [],
          );
          this.first_flight_offer = this.flight_offers[0] || {};
          this.travelers = get_object_field(response, "voucher.travelers", []);
          this.contacts = get_object_field(response, "voucher.contacts", []);
          this.tickets = get_object_field(response, "voucher.tickets", []);
          this.automated_checkin_info = get_object_field(response, "voucher.automated_checkin_info", null);
          this.voucher = response.voucher;
          this.checkin_online_info = response.checkinOnlineInfo || "";
          this.beneficiary = response.beneficiary;
          this.status = response.status;

          if (response.postSellNotice) {
            this.post_sell_notice = response.postSellNotice;
          }
        },
        (error) => {
          this.on_error(error);
        },
      )
      .catch((error) => {
        this.on_error(error);
      })
      .finally(() => {
        this.scroll_top();
        this.loading = false;
      });
  },
  methods: {
    on_error(error) {
      log("confirm.get_confirmation", error);
      this.confirm = {};
      if (this.redirect_interval) {
        clearInterval(this.redirect_interval);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  .confirm {
    margin-bottom: $grid-gutter-width * 4;

    &--panel {
      @extend .content-panel !optional;
      margin-bottom: 1rem !important;
    }

    &__heading {
      margin-top: $grid-gutter-width * 2;
      margin-bottom: $grid-gutter-20 * 2;
      color: $primary;

      &-title {
        font-weight: $vb-font-weight-semibold;
        margin-bottom: 0;
      }

      &-subtitle {
        font-size: $font-size-xl;
        font-weight: $vb-font-weight-semibold;
      }

      &-desc{
        color: $gray-900;
        margin-bottom: 0;
      }
    }

    &__elements {
      &--first {
        margin-bottom: $grid-gutter-20;

        @include media-breakpoint-up(xl) {
          display: flex;
          align-self: stretch;

          ::v-deep {
            .content-section {
              height: 100%;
              margin-bottom: 0;
            }
          }

          .reservation {
            flex-basis: 50%;
            margin-right: $grid-gutter-20 / 2
          }
          .flight-summary {
            flex-basis: 50%;
            margin-left: $grid-gutter-20 / 2
          }
        }
      }
    }

    .alert {
      margin-bottom: $grid-gutter-20
    }

    /*// reservation e flight-summary affiancati (xl): stessa altezza come nel checkout
    .reservation,
    .flight-summary {
      height: 100%;

      .content-section {
        height: 100%;
      }
    }*/
  }
</style>
